export const CHECK_IN_CHECKOUT_MIN_PERIODS = 2
const CHECK_IN_EVENT = "check-in"
const CHECK_OUT_EVENT = "check-out"

export type CheckInCheckOutEvent = {
    id?: number
    eventType?: string
    createdAt?: string | null
    dateTimeRounded?: string | null
}

export type CheckInCheckOutPeriod = {
    checkInAt?: string | null
    checkOutAt?: string | null
}

export type CheckInCheckOutStudent = {
    profileId?: number
    customProfileId?: string
    customUserId?: string
    studentName?: string
    firstName?: string
    lastName?: string
    date?: string
    timezone?: string
    attendedHours?: number
    missedHours?: number
    scheduleHours?: number
    isAbsent?: boolean
    periods?: CheckInCheckOutPeriod[]
}

export const isCheckInCheckOutAbsent = (student: CheckInCheckOutStudent): boolean => {
    if (typeof student.isAbsent === "boolean") {
        return student.isAbsent
    }
    return !(student.periods ?? []).length
}

export const getCheckInCheckOutPeriodCount = (students: CheckInCheckOutStudent[] = []): number => {
    const maxPeriods = students.reduce((max, student) => Math.max(max, student.periods?.length ?? 0), 0)
    return Math.max(CHECK_IN_CHECKOUT_MIN_PERIODS, maxPeriods)
}

const eventTime = (event: CheckInCheckOutEvent) => event.dateTimeRounded || event.createdAt

export const buildCheckInCheckOutPeriods = (events: CheckInCheckOutEvent[] = []): CheckInCheckOutPeriod[] => {
    const relevant = events
        .filter((event) => event.eventType === CHECK_IN_EVENT || event.eventType === CHECK_OUT_EVENT)
        .slice()
        .sort((a, b) => {
            const aTime = new Date(eventTime(a) ?? 0).getTime()
            const bTime = new Date(eventTime(b) ?? 0).getTime()
            if (aTime !== bTime) {
                return aTime - bTime
            }
            return (a.id ?? 0) - (b.id ?? 0)
        })

    const periods: CheckInCheckOutPeriod[] = []
    let current: CheckInCheckOutPeriod | null = null

    for (const event of relevant) {
        if (event.eventType === CHECK_IN_EVENT) {
            if (current) {
                periods.push(current)
            }
            current = {
                checkInAt: eventTime(event) ?? null,
                checkOutAt: null
            }
            continue
        }

        if (current) {
            current.checkOutAt = eventTime(event) ?? null
            periods.push(current)
            current = null
            continue
        }

        periods.push({
            checkInAt: null,
            checkOutAt: eventTime(event) ?? null
        })
    }

    if (current) {
        periods.push(current)
    }

    return periods
}

const roundTwoDigits = (num: number) => {
    if (typeof num === "number" && num !== 0) {
        return Math.round(num * 100) / 100
    }
    return 0
}

const toUtcMinute = (value: string) => {
    const date = new Date(value)
    return Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes()
    )
}

export const getAttendedHoursFromPeriods = (periods: CheckInCheckOutPeriod[] = []): number => {
    let minutes = 0

    for (const period of periods) {
        if (!period.checkInAt || !period.checkOutAt) {
            continue
        }

        const durationMinutes = (toUtcMinute(period.checkOutAt) - toUtcMinute(period.checkInAt)) / 60000
        if (durationMinutes > 0) {
            minutes += durationMinutes
        }
    }

    return roundTwoDigits(minutes / 60)
}
